// src/pages/IpdReports.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import Select from "react-select";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// ---------- Helpers ----------
const pad = (n) => String(n).padStart(2, "0");
const getTodayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const getNowHHMM = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function formatTime12(time24) {
  if (!time24) return "";
  if (time24 instanceof Date) {
    let h = time24.getHours();
    const m = pad(time24.getMinutes());
    h = h % 12 || 12;
    const ampm = time24.getHours() >= 12 ? "PM" : "AM";
    return `${pad(h)}:${m} ${ampm}`;
  }
  const t = String(time24).trim();
  const m = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return t;
  let hh = Number(m[1]);
  const mm = pad(Number(m[2]));
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = hh % 12 || 12;
  return `${pad(hh)}:${mm} ${ampm}`;
}

function parseServerDateToLocal(s) {
  if (!s) return null;
  if (s instanceof Date) return s;
  const normalized = String(s).trim().replace(" ", "T");
  const m = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (!m) {
    const fallback = new Date(s);
    return isNaN(fallback) ? null : fallback;
  }
  const year = Number(m[1]),
    month = Number(m[2]) - 1,
    day = Number(m[3]);
  const hour = Number(m[4] ?? 0),
    minute = Number(m[5] ?? 0),
    second = Number(m[6] ?? 0);
  return new Date(year, month, day, hour, minute, second);
}

function formatDateTime12(s) {
  const dt = parseServerDateToLocal(s);
  if (!dt) return "";
  const day = pad(dt.getDate());
  const months = [
    "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"
  ];
  const mon = months[dt.getMonth()];
  const year = dt.getFullYear();
  let hr = dt.getHours();
  const min = pad(dt.getMinutes());
  hr = hr % 12 || 12;
  const ampm = dt.getHours() >= 12 ? "PM" : "AM";
  return `${day}-${mon}-${year} ${pad(hr)}:${min} ${ampm}`;
}

// ---------- Component (IPD reports with doctor commission pay) ----------
export default function IpdReports() {
  const [activeTab, setActiveTab] = useState("discharged");
  const [filters, setFilters] = useState({
    dischargeFrom: getTodayYMD(),
    dischargeFromTime: "00:00",
    dischargeTo: getTodayYMD(),
    dischargeToTime: getNowHHMM(),
    doctorId: "",
    doctorSearch: "",
    search: "",
    status: "1"
  });

  const [records, setRecords] = useState([]);
  const [docsFromServer, setDocsFromServer] = useState([]);
  const [totals, setTotals] = useState({
    totalIpdBill: "0.00",
    totalAdvance: "0.00",
    paidCash: "0.00",
    paidCard: "0.00",
    paidOnline: "0.00",
    totalDue: "0.00",
    totalPatients: 0,
    totalCommission: "0.00"
  });

  // --- Doctor pay modal states ---
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payProcessing, setPayProcessing] = useState(false);
  const [payError, setPayError] = useState("");

  // Prefer ipd_bill_commission directly; fallback to other known fields only if missing
  const getCommissionForRow = (r) => {
    if (!r) return 0;
    const direct = r.ipd_bill_commission ?? r.ipd_bill_commission_doctor ?? r.ipd_bill_commission_amount;
    if (typeof direct !== "undefined" && direct !== null && direct !== "") {
      const n = Number(direct);
      if (!isNaN(n)) return n;
      const parsed = parseFloat(String(direct).replace(/[^0-9.-]/g, ""));
      if (!isNaN(parsed)) return parsed;
    }
    const candidates = [
      r.ipd_commission,
      r.commission,
      r.doctor_commission,
      r.ipd_bill_commission_doctor_amount
    ];
    for (const c of candidates) {
      if (typeof c !== "undefined" && c !== null && c !== "") {
        const n = Number(c);
        if (!isNaN(n)) return n;
        const parsed = parseFloat(String(c).replace(/[^0-9.-]/g, ""));
        if (!isNaN(parsed)) return parsed;
      }
    }
    return 0;
  };

  // compute paid/due for doctor commission for a row
  const getDoctorNumbers = (r) => {
    // check possible paid keys returned by backend
    const paidCandidates = [
      r.ipd_commission_paid,
      r.ipd_bill_commission_paid,
      r.doctor_commission_paid,
      r.ipd_doctor_commission_paid,
      r.commission_paid
    ];
    let paid = 0;
    for (const p of paidCandidates) {
      if (typeof p !== "undefined" && p !== null && p !== "") {
        const n = Number(p);
        if (!isNaN(n)) {
          paid = n;
          break;
        }
      }
    }
    // if backend provided payment list qty or aggregated field differently, you might adapt
    const commission = getCommissionForRow(r);
    const due = Math.max(commission - (paid || 0), 0);
    return { commission: Number(commission || 0), paid: Number(paid || 0), due: Number(due || 0) };
  };

  const getDoctorStatusInfo = (r) => {
    const { commission, paid, due } = getDoctorNumbers(r);
    if (commission <= 0) return { status: "N/A", className: "bg-secondary" };
    if (paid <= 0) return { status: "Not Paid", className: "bg-danger" };
    if (due <= 0.01) return { status: "Paid", className: "bg-success" };
    return { status: "Partial", className: "bg-warning text-dark" };
  };

  // ---------- Export PDF (keeps commission columns) ----------
  const handleExportPdf = async () => {
    try {
      const rows = filteredRecords;
      if (!rows || rows.length === 0) {
        Swal.fire('Nothing to export', 'No records available to export.', 'info');
        return;
      }

      const doc = new jsPDF('landscape', 'pt', 'a4');
      const title = `IPD Discharged Report`;
      const subtitle = `Date: ${getTodayYMD()}   Doctor: ${filters.doctorSearch || 'All'}`;

      doc.setFontSize(14);
      doc.text(title, 40, 40);
      doc.setFontSize(10);
      doc.text(subtitle, 40, 60);

      const summaryY = 78;
      doc.setFontSize(9);
      doc.text(
        `Total Patients: ${totals.totalPatients}   Total IPD Bill: ₹${totals.totalIpdBill}   Commission: ₹${totals.totalCommission}   Paid (Cash): ₹${totals.paidCash}   Paid (Online): ₹${totals.paidOnline}   Paid (Card): ₹${totals.paidCard}`,
        40,
        summaryY
      );

      const head = [
        ['SL','IPD No','UHID','Patient Name','Discharge Date','Commission','Paid','Due','IPD Bill','Cash','Card','Online','Due']
      ];

      const body = rows.map((r, idx) => {
        const ipdNo = `000${r.ipd_id ?? r.id ?? ''}`;
        const uhid = (r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '';
        const name = (r.ipd_patient_name || '').replace(/\s+/g,' ');
        const discharge = formatDateTime12(r.ipd_discharge_date || r.discharge_datetime || r.discharge_date || '');
        const { commission, paid, due } = getDoctorNumbers(r);
        const ipdBill = Number(r.ipd_bill_total_final ?? r.ipd_ipd_bill_amount ?? r.ipd_bill_amount ?? 0).toFixed(2);
        const cash = Number(r.ipd_paid_cash ?? 0).toFixed(2);
        const card = Number(r.ipd_paid_card ?? 0).toFixed(2);
        const online = Number(r.ipd_paid_online ?? 0).toFixed(2);
        const dueBill = Number(r.ipd_bill_total_due ?? r.ipd_due ?? r.due ?? 0).toFixed(2);

        return [
          String(idx + 1),
          ipdNo,
          uhid,
          name,
          discharge,
          commission.toFixed(2),
          paid.toFixed(2),
          due.toFixed(2),
          ipdBill,
          cash,
          card,
          online,
          dueBill
        ];
      });

      autoTable(doc, {
        startY: 95,
        head: head,
        body: body,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [241,245,251], textColor: 60, halign: 'center' },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 60 },
          2: { cellWidth: 80 },
          3: { cellWidth: 170 },
          4: { cellWidth: 90 },
          5: { cellWidth: 70, halign: 'right' },
          6: { cellWidth: 70, halign: 'right' },
          7: { cellWidth: 70, halign: 'right' },
          8: { cellWidth: 70, halign: 'right' },
          9: { cellWidth: 60, halign: 'right' },
          10: { cellWidth: 60, halign: 'right' },
          11: { cellWidth: 60, halign: 'right' },
          12: { cellWidth: 60, halign: 'right' }
        },
        didDrawPage: (data) => {
          const pageCount = doc.getNumberOfPages();
          const page = doc.getCurrentPageInfo().pageNumber;
          doc.setFontSize(9);
          doc.text(`Page ${page} of ${pageCount}`, doc.internal.pageSize.width - 60, doc.internal.pageSize.height - 10, { align: 'right' });
        },
        margin: { left: 40, right: 20, top: 40 }
      });

      doc.save(`ipd_discharged_${getTodayYMD()}.pdf`);
    } catch (err) {
      console.error('PDF export failed', err);
      Swal.fire('Export failed', 'Could not generate PDF in browser. See console.', 'error');
    }
  };

  // ---------- Fetching ----------
  const loadRecords = async () => {
    try {
      const fd = new FormData();
      fd.append("page_no", "none");

      if (filters.dischargeFrom) fd.append("dischargeFrom", filters.dischargeFrom);
      if (filters.dischargeFromTime) fd.append("dischargeFromTime", filters.dischargeFromTime);
      if (filters.dischargeTo) fd.append("dischargeTo", filters.dischargeTo);
      if (filters.dischargeToTime) fd.append("dischargeToTime", filters.dischargeToTime);

      if (typeof filters.status !== "undefined" && filters.status !== "") {
        fd.append("status", String(filters.status));
      }

      if (filters.search) fd.append("search_key", filters.search);
      if (filters.doctorId) fd.append("doctor_id", filters.doctorId);

      const url = AppConst.BASE_URL + (AppConst.getIpdDischargedUrl || AppConst.getIpdListUrl2);
      const res = await axios.post(url, fd);

      if (res?.data?.status && Array.isArray(res.data.records)) {
        setRecords(res.data.records);
        const t = res.data.totals || {};
        setTotals(prev => ({
          ...prev,
          totalIpdBill: (t.sum_ipd_bill ?? t.sum_ipd_bill ?? 0).toFixed(2),
          paidCash: (t.total_cash ?? t.paidCash ?? 0).toFixed(2),
          paidCard: (t.total_card ?? t.paidCard ?? 0).toFixed(2),
          paidOnline: (t.total_online ?? t.paidOnline ?? 0).toFixed(2),
          totalDue: (t.sum_due ?? t.total_due ?? 0).toFixed(2),
          totalPatients: (t.count ?? (res.data.records || []).length)
        }));
      } else {
        setRecords([]);
        setTotals({ totalIpdBill: "0.00", totalAdvance: "0.00", paidCash: "0.00", paidCard: "0.00", paidOnline: "0.00", totalDue: "0.00", totalPatients: 0, totalCommission: "0.00" });
      }
    } catch (e) {
      console.error("loadRecords error:", e);
      setRecords([]);
    }
  };

  function renderPayModes(record) {
    const raw = record.ipd_bill_pay_modes || '';
    const parts = raw.split(',').map(p => p.trim()).filter(Boolean);
    const mapToLabel = (m) => {
      if (!m) return '';
      m = m.toLowerCase();
      if (m.includes('upi') || m.includes('netbank')) return 'online';
      if (m.includes('card')) return 'card';
      if (m.includes('cash')) return 'cash';
      return m;
    };
    const normalized = Array.from(new Set(parts.map(mapToLabel)));
    const order = ['card','online','cash'];
    normalized.sort((a,b) => order.indexOf(a) - order.indexOf(b));
    return normalized.map((m,i) => {
      const label = m.charAt(0).toUpperCase() + m.slice(1);
      const icon = m === 'cash' ? '💵' : (m === 'card' ? '💳' : '🌐');
      return (
        <span key={i} className="badge badge-sm mr-1" style={{background:'#e9ecef', color:'#333'}}>
          {icon} {label}
        </span>
      );
    });
  }

  const fetchDoctors = async () => {
    try {
      const fd = new FormData();
      fd.append("type", "doctor");
      fd.append("page_no", "none");
      const res = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd);
      if (res?.data?.status && Array.isArray(res.data.records)) {
        setDocsFromServer(res.data.records);
      }
    } catch (e) {
      console.error("fetchDoctors error:", e);
    }
  };

  useEffect(() => {
    loadRecords();
    fetchDoctors();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadRecords(), 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters]);

  const getDoctorNameById = (id, r = null) => {
    if (!id && !r) return "";
    if (r) {
      const friendlyKeys = [
        'ipd_refer_doctor_name','ipd_bill_refered_doctor_name','ipd_refer_doctor_name','ipd_ref_doc_name','ipd_cons_doc_name','consultant_name','doctor_name'
      ];
      for (const k of friendlyKeys) {
        if (r[k]) return String(r[k]);
      }
    }
    if (!id && !r) return "";
    let candidate = id;
    if (typeof candidate === 'string' && candidate.indexOf(',') !== -1) {
      candidate = candidate.split(',')[0].trim();
    }
    const doc = docsFromServer.find(d => String(d.user_id) === String(candidate) || String(d.id) === String(candidate));
    if (doc) return doc.fname || doc.name || doc.username || String(candidate);
    if (r) {
      const altKeys = ['ref_doc_name','refer_doctor_name','doctor','ref_doc','ref_doc_id'];
      for (const k of altKeys) {
        if (r[k]) return String(r[k]);
      }
    }
    return candidate ? String(candidate) : '';
  };

  const getDoctorNameForRow = (row) => {
    if (!row) return "";
    const friendly = row.ipd_bill_refered_doctor_name || row.ipd_refer_doctor_name || row.ipd_ref_doc_name || row.ipd_cons_doc_name || row.consultant_name;
    if (friendly) return String(friendly);
    const id = row.ipd_bill_refered_doctor ?? row.ipd_refer_doctor ?? row.ipd_ref_doc ?? row.ipd_cons_doc ?? row.refer_doctor ?? null;
    if (id) return getDoctorNameById(id, row);
    return "";
  };

  // ---------- Filtering (discharge-only) ----------
  const filteredRecords = useMemo(() => {
    let data = [...records];
    data = data.filter(r => String(r.status) === String(filters.status));

    if (filters.doctorId && filters.doctorId !== "") {
      const sel = String(filters.doctorId).toLowerCase();
      data = data.filter(r => {
        const idMatch = String(r.ipd_bill_refered_doctor ?? r.ipd_refer_doctor ?? r.ipd_ref_doc ?? r.ipd_cons_doc ?? '').toLowerCase() === sel;
        const nameMatch = (getDoctorNameForRow(r) || '').toLowerCase().includes(sel);
        return idMatch || nameMatch;
      });
    }

    if (filters.search && filters.search.trim() !== "") {
      const skRaw = filters.search.trim();
      const sk = skRaw.toLowerCase();
      const digitsOnly = skRaw.replace(/\D/g, '');

      data = data.filter(r => {
        const uhid = ((r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '').toString().toLowerCase();
        const name = (r.ipd_patient_name || '').toString().toLowerCase();
        const phones = [
          r.ipd_patient_mobile,
          r.ipd_cont_number,
          r.ipd_patient_phone,
          r.patient_mobile,
          r.mobile,
          r.phone
        ].filter(Boolean).map(p => String(p).toLowerCase());
        const ipdNoDisplayed = `000${r.ipd_id ?? r.id ?? ''}`.toLowerCase();

        if (name.includes(sk)) return true;
        if (uhid.includes(sk)) return true;
        if (ipdNoDisplayed.includes(sk)) return true;
        for (const ph of phones) {
          if (ph.includes(sk)) return true;
          if (digitsOnly && ph.replace(/\D/g, '').includes(digitsOnly)) return true;
        }
        if (digitsOnly) {
          const ipdIdStr = String(r.ipd_id ?? r.id ?? '').replace(/\D/g, '');
          if (ipdIdStr.includes(digitsOnly)) return true;
        }
        return false;
      });
    }

    if (filters.dischargeFrom || filters.dischargeTo) {
      const fromDt = filters.dischargeFrom ? new Date(`${filters.dischargeFrom}T${filters.dischargeFromTime || '00:00'}`) : null;
      const toDt = filters.dischargeTo ? new Date(`${filters.dischargeTo}T${filters.dischargeToTime || '23:59'}`) : null;
      data = data.filter(r => {
        const ds = r.ipd_discharge_date || r.discharge_datetime || r.discharge_date || null;
        const dt = parseServerDateToLocal(ds);
        if (!dt) return false;
        if (fromDt && dt < fromDt) return false;
        if (toDt && dt > toDt) return false;
        return true;
      });
    }

    return data;
  }, [records, filters, docsFromServer]);

  // ---------- Totals computed from filtered list (discharged only) ----------
  useEffect(() => {
    const num = (v) => { const n = Number(v ?? 0); return isNaN(n) ? 0 : n; };
    let totalIpd = 0, totalAdvance = 0, paidCash = 0, paidCard = 0, paidOnline = 0, totalDue = 0, totalCommission = 0;

    filteredRecords.forEach(r => {
      const advance = num(r.ipd_advance ?? r.advance ?? r.advance_amt ?? 0);
      const ipdBill = num(r.ipd_bill_total_final ?? r.ipd_ipd_bill_amount ?? r.ipd_bill_amount ?? r.ipd_final_amt ?? 0);
      const due = num(r.ipd_bill_total_due ?? r.ipd_due ?? r.due ?? 0);
      const comm = getCommissionForRow(r);

      totalAdvance += advance;
      totalIpd += ipdBill;
      totalDue += due;
      totalCommission += Number(comm ?? 0);

      paidCash += num(r.ipd_paid_cash ?? r.paid_cash ?? r.paidCash ?? 0);
      paidCard += num(r.ipd_paid_card ?? r.paid_card ?? r.paidCard ?? 0);
      paidOnline += num(r.ipd_paid_online ?? r.paid_online ?? r.paidOnline ?? 0);
    });

    setTotals(prev => ({
      ...prev,
      totalIpdBill: totalIpd.toFixed(2),
      totalAdvance: totalAdvance.toFixed(2),
      paidCash: paidCash.toFixed(2),
      paidCard: paidCard.toFixed(2),
      paidOnline: paidOnline.toFixed(2),
      totalDue: totalDue.toFixed(2),
      totalPatients: filteredRecords.length,
      totalCommission: totalCommission.toFixed(2)
    }));
  }, [filteredRecords]);

  // ---------- Handlers ----------
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Open pay modal for a selected IPD row (doctor commission)
  const openPayModal = (row) => {
    if (!row) return;
    const { due } = getDoctorNumbers(row);
    setPayRow(row);
    setPayAmount(due > 0 ? due.toFixed(2) : "");
    setPayError("");
    setPayModalOpen(true);
  };

  const closePayModal = () => {
    setPayRow(null);
    setPayAmount("");
    setPayError("");
    setPayModalOpen(false);
  };

  // Submit doctor commission payment for IPD (backend endpoint: AppConst.payIpdDoctorCommissionUrl)
  const handlePaySubmit = async (e) => {
    e.preventDefault();
    if (!payRow) return;
    const { commission, paid, due } = getDoctorNumbers(payRow);
    const amt = parseFloat(payAmount || 0);

    if (isNaN(amt) || amt <= 0) {
      setPayError("Please enter a valid amount.");
      return;
    }
    if (amt > due + 0.01) {
      setPayError("Amount cannot be more than due.");
      return;
    }

    try {
      setPayProcessing(true);
      setPayError("");

      const fd = new FormData();
      // send ipd identifier - backend should accept ipd_id or ipd_no or uhid according to your API contract
      if (payRow.ipd_id) fd.append("ipd_id", String(payRow.ipd_id));
      else if (payRow.id) fd.append("ipd_id", String(payRow.id));
      if (payRow.ipd_uhid) fd.append("ipd_uhid", String(payRow.ipd_uhid));

      fd.append("pay_amount", amt.toFixed(2));
      fd.append("note", `IPD doctor commission payment via UI`);

      const res = await axios.post(AppConst.BASE_URL + (AppConst.payIpdDoctorCommissionUrl), fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.data?.status) {
        Swal.fire("Saved", res.data.message || "Payment saved", "success");
        closePayModal();
        loadRecords(); // refresh
      } else {
        setPayError(res.data?.message || "Failed to save payment");
      }
    } catch (err) {
      console.error("pay ipd doctor error", err);
      setPayError(err.response?.data?.message || err.message || "Failed to save payment.");
    } finally {
      setPayProcessing(false);
    }
  };

  // ----- Columns -----
  const getDoctorOptions = () => docsFromServer.map(d => ({ value: String(d.user_id), label: d.fname || d.name || d.username || String(d.user_id) }));

  const viewButtonColumn = {
    name: 'View',
    width: '100px',
    cell: (r) => (
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={(e) => { e.stopPropagation(); openPatientCard(r); }}
      >
        View
      </button>
    )
  };

  const commonColumns = [
    { name: 'SL', selector: (r,i) => i+1, width: '60px' },
    { name: 'IPD No', selector: r => `000${r.ipd_id ?? r.id ?? ''}`, sortable: true, width: '140px' },
    { name: 'UHID', selector: r => (r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '', sortable: true, width: '150px' },
    { name: 'Patient Name', selector: r => `${r.ipd_patient_title || ''} ${r.ipd_patient_name || ''}`, cell: r => (<div>{r.ipd_patient_name}<br/><small className="text-muted">{r.ipd_patient_mobile || r.ipd_cont_number}</small></div>), wrap: true, width: '220px' },
    { name: 'Bed', selector: r => r.ipd_bed_name || r.ipd_bed || '', sortable: true, width: '140px' },
    { name: 'Referred Doctor', selector: r => getDoctorNameForRow(r), sortable: true, width: '200px' },
    { name: 'OT Doctor', selector: r => getDoctorNameById(r.ot_doctor), width: '200px' }
  ];

  const dischargedColumns = [
    ...commonColumns.slice(0,3),
    { name: 'Discharge Date', selector: r => r.ipd_discharge_date || r.discharge_datetime || '', sortable: true, cell: r => formatDateTime12(r.ipd_discharge_date || r.discharge_datetime), width: '200px' },
    ...commonColumns.slice(3),
    // Commission column
    {
      name: 'Commission',
      selector: r => getCommissionForRow(r).toFixed(2),
      right: true,
      width: '110px',
      cell: r => {
        const val = Number(getCommissionForRow(r) ?? 0);
        const text = `₹${val.toFixed(2)}`;
        const style = {};
        if (val === 0) {
          style.color = '#c92a2a';
          style.fontWeight = 600;
        } else if (val > 1) {
          style.color = '#2f9e44';
          style.fontWeight = 600;
        }
        return <span style={style}>{text}</span>;
      }
    },
    {
      name: 'Doc Paid',
      selector: r => getDoctorNumbers(r).paid.toFixed(2),
      right: true,
      width: '110px',
      cell: r => <span>₹{getDoctorNumbers(r).paid.toFixed(2)}</span>
    },
    {
      name: 'Doc Due',
      selector: r => getDoctorNumbers(r).due.toFixed(2),
      right: true,
      width: '110px',
      cell: r => <span className={getDoctorNumbers(r).due > 0 ? "text-danger fw-bold" : ""}>₹{getDoctorNumbers(r).due.toFixed(2)}</span>
    },
    {
      name: 'Status',
      selector: r => getDoctorStatusInfo(r).status,
      width: '110px',
      cell: r => {
        const info = getDoctorStatusInfo(r);
        return <span className={`badge rounded-pill ${info.className}`}>{info.status}</span>;
      }
    },
    {
      name: 'Action',
      width: '100px',
      cell: (r) => {
        const { due } = getDoctorNumbers(r);
        const disabled = due <= 0.01;
        return (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={(e) => { e.stopPropagation(); openPayModal(r); }} disabled={disabled}>
            {disabled ? "Paid" : "Pay"}
          </button>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    { name: 'IPD Bill', selector: r => Number(r.ipd_bill_paid ?? r.ipd_bill_total_final ?? r.ipd_bill_total_paid ?? 0).toFixed(2), right: true, width: '120px' },
    { name: 'Paid (Cash)', selector: r => Number(r.ipd_paid_cash ?? 0).toFixed(2), right: true, width: '110px' },
    { name: 'Paid (Card)', selector: r => Number(r.ipd_paid_card ?? 0).toFixed(2), right: true, width: '110px' },
    { name: 'Paid (Online)', selector: r => Number(r.ipd_paid_online ?? 0).toFixed(2), right: true, width: '110px' },
    { name: 'Due', selector: r => Number(r.ipd_bill_total_due  ?? r.ipd_due  ?? r.due ?? 0).toFixed(2), right: true, width: '110px' },
    { name: 'User', selector: r => r.last_used || r.user_display || r.fname || '', width: '160px' }
  ];

  const columns = [viewButtonColumn, ...dischargedColumns];

  const customStyles = {
    headCells: { style: { backgroundColor: '#f1f5fb', color:'#405189', fontSize:'15px', fontWeight:700 } },
    rows: { style: { minHeight: '56px' } },
    cells: { style: { paddingLeft: '12px', paddingRight:'12px' } }
  };

  // ----- UI helpers reused from previous file -----
  const openPatientCard = async (row) => {
    if (!row) return;
    const esc = (s) => {
      if (s === null || typeof s === 'undefined') return '';
      return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#039;');
    };

    const uhid = row.ipd_uhid || row.opd_uhid_move || row.opd_uhid || '';
    const wardName = row.ipd_ward_name || row.ward_name || row.ipd_ward || 'N/A';
    const bedName  = row.ipd_bed_name  || row.bed_name || row.ipd_bed || 'N/A';
    const referred = getDoctorNameForRow(row) || row.ipd_refer_doctor || '';
    const regDate = formatDateTime12(row.ipd_datetime);
    const discharge = formatDateTime12(row.discharge_datetime);
    const otDoctor = row.ot_doctor_name || getDoctorNameById(row.ot_doctor) || row.ipd_ot_doctor || row.ipd_anesthesia_doctor || '';

    const patientHtml = `
      <div style="text-align:left">
        <p><strong>UHID:</strong> ${esc(uhid) || 'N/A'}</p>
        <p><strong>IPD No:</strong> ${esc(row.ipd_id ?? row.id ?? 'N/A')}</p>
        <p><strong>Name:</strong> ${esc(row.ipd_patient_name ?? 'N/A')}</p>
        <p><strong>Phone:</strong> ${esc(row.ipd_patient_mobile ?? row.ipd_cont_number ?? 'N/A')}</p>
        <p><strong>Ward:</strong> ${esc(wardName)} &nbsp; <strong>Bed:</strong> ${esc(bedName)}</p>
        <p><strong>Admission:</strong> ${esc(regDate)}</p>
        <p><strong>Discharge:</strong> ${esc(discharge)}</p>
        <p><strong>Referred Doctor:</strong> ${esc(referred || '-')}</p>
         <p><strong>Surgeon/OT Doctor:</strong> ${esc(otDoctor)}</p>
        <p><strong>Reg Charges:</strong> ${Number(row.ipd_reg_charges ?? 0).toFixed(2)}</p>
        <p><strong>Advance:</strong> ${Number(row.ipd_advance ?? 0).toFixed(2)}</p>
        <p><strong>IPD Total Paid: (Reg. Charges + Advance) </strong> ${Number(row.ipd_total_paid ?? row.paid ?? row.ipd_bill_total_paid ?? 0).toFixed(2)} &nbsp; <strong>Due:</strong> ${Number(row.ipd_due ?? row.due ?? row.ipd_bill_total_due ?? 0).toFixed(2)}</p>
      <p><strong>IPD Bill Paid:</strong> ${Number(row.ipd_bill_paid ?? 0).toFixed(2)} &nbsp; <strong>IPD Bill Due:</strong> ${Number(row.ipd_bill_due ?? 0).toFixed(2)}</p>
        </div>
    `;

    try {
      const fd = new FormData();
      if (row.ipd_id) fd.append('ipd_id', String(row.ipd_id));
      if (row.ipd_uhid) fd.append('ipd_uhid', String(row.ipd_uhid));
      if (row.ipd_no) fd.append('ipd_no', String(row.ipd_no));

      const itemsUrl = AppConst.BASE_URL + (AppConst.getIpdBillItemsUrl || '/api/get_ipd_bill_items');
      const res = await axios.post(itemsUrl, fd);

      let items = [];
      if (res?.data) {
        if (Array.isArray(res.data.items)) items = res.data.items;
        else if (Array.isArray(res.data.records)) items = res.data.records;
        else if (Array.isArray(res.data.data)) items = res.data.data;
        else if (Array.isArray(res.data)) items = res.data;
      }

      let itemsTableHtml = `<div class="mt-2"><strong>Services:</strong><div>No services found</div></div>`;
      if (items && items.length > 0) {
        const rowsHtml = items.map(it => {
          const svc = esc(it.ipd_bill_item_service ?? it.service ?? it.name ?? it.item_name ?? 'Unknown service');
          const qty = Number(it.ipd_bill_item_qty ?? it.qty ?? it.quantity ?? 0);
          const charge = Number(it.ipd_bill_item_charge ?? it.charge ?? it.price ?? 0);
          const dis = Number(it.ipd_bill_item_dis ?? it.discount ?? it.ipd_bill_item_discount ?? 0);
          const finalAmt = Number(it.ipd_bill_item_final ?? it.final ?? it.ipd_bill_item_amount ?? 0);

          return `<tr><td style="padding:6px 10px;border-bottom:1px solid #eee">${svc}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:center">${qty}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${charge.toFixed(2)}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${dis.toFixed(2)}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${finalAmt.toFixed(2)}</td></tr>`;
        }).join('');

        itemsTableHtml = `
          <div class="mt-2" style="width:100%;overflow:auto">
            <strong>Services:</strong>
            <table style="width:100%;border-collapse:collapse;margin-top:6px">
              <thead>
                <tr style="background:#f7f9fc;">
                  <th style="text-align:left;padding:8px 10px;border-bottom:1px solid #ddd">Service</th>
                  <th style="text-align:center;padding:8px 10px;border-bottom:1px solid #ddd;width:80px">Qty</th>
                  <th style="text-align:right;padding:8px 10px;border-bottom:1px solid #ddd;width:110px">Charge</th>
                  <th style="text-align:right;padding:8px 10px;border-bottom:1px solid #ddd;width:100px">Disc</th>
                  <th style="text-align:right;padding:8px 10px;border-bottom:1px solid #ddd;width:110px">Final</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>
          </div>
        `;
      }

      const finalHtml = `<div>${patientHtml}${itemsTableHtml}</div>`;

      Swal.fire({
        title: `Patient: ${esc(row.ipd_patient_name ?? '')}`,
        html: finalHtml,
        width: 900,
        showCloseButton: true,
        confirmButtonText: 'Close'
      });
    } catch (err) {
      console.warn('openPatientCard: failed to fetch bill items', err);
      Swal.fire({
        title: `Patient: ${esc(row.ipd_patient_name ?? '')}`,
        html: patientHtml + `<div class="mt-2 text-danger">Could not load services</div>`,
        width: 900,
        showCloseButton: true,
        confirmButtonText: 'Close'
      });
    }
  };

  // ---------- Render ----------
  return (
    <div className="page-content">
      <div className="container-fluid">

        <ul className="nav nav-pills nav-customs nav-primary mb-3" role="tablist">
          <li className="nav-item">
            <a
              className={`nav-link ${activeTab === "discharged" ? "active" : ""}`}
              onClick={() => setActiveTab("discharged")}
              href="#"
            >
              IPD Discharge Patient
            </a>
          </li>
        </ul>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-3">
                <label>Search</label>
                <input name="search" value={filters.search} onChange={handleFilterChange} className="form-control" placeholder="UHID / Name / Mobile"/>
              </div>

              <div className="col-md-2">
                <label>Discharge From</label>
                <input type="date" name="dischargeFrom" value={filters.dischargeFrom} onChange={handleFilterChange} className="form-control" />
              </div>

              <div className="col-md-1">
                <label>Time</label>
                <input type="time" name="dischargeFromTime" value={filters.dischargeFromTime} onChange={handleFilterChange} className="form-control" />
                <small className="text-muted d-block text-center">{formatTime12(filters.dischargeFromTime)}</small>
              </div>

              <div className="col-md-2">
                <label>Discharge To</label>
                <input type="date" name="dischargeTo" value={filters.dischargeTo} onChange={handleFilterChange} className="form-control" />
              </div>

              <div className="col-md-1">
                <label>Time</label>
                <input type="time" name="dischargeToTime" value={filters.dischargeToTime} onChange={handleFilterChange} className="form-control" />
                <small className="text-muted d-block text-center">{formatTime12(filters.dischargeToTime)}</small>
              </div>

              <div className="col-md-3">
                <label>Referred Doctor</label>
                <Select
                  isClearable
                  value={docsFromServer.find(d => String(d.user_id) === String(filters.doctorId)) ? { value: String(filters.doctorId), label: (docsFromServer.find(d => String(d.user_id) === String(filters.doctorId)).fname || docsFromServer.find(d => String(d.user_id) === String(filters.doctorId)).name || docsFromServer.find(d => String(d.user_id) === String(filters.doctorId)).username) } : null}
                  onChange={(sel) => {
                    if (!sel) setFilters(prev => ({ ...prev, doctorId: "", doctorSearch: "" }));
                    else setFilters(prev => ({ ...prev, doctorId: String(sel.value), doctorSearch: String(sel.label) }));
                  }}
                  options={getDoctorOptions()}
                  placeholder="Select doctor..."
                />
                <small className="text-muted">Select a doctor to filter</small>
              </div>

            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">

            <div className="d-flex justify-content-end mb-2">
              <button className="btn btn-outline-primary" onClick={handleExportPdf}>Export PDF</button>
            </div>

            <DataTable
              columns={columns}
              data={filteredRecords}
              customStyles={customStyles}
              pagination
              highlightOnHover
              persistTableHead
              responsive
              onRowClicked={async (row) => {
                try {
                  const fd = new FormData();
                  if (row.ipd_id) fd.append('ipd_id', String(row.ipd_id));
                  if (row.ipd_uhid) fd.append('ipd_uhid', String(row.ipd_uhid));
                  if (row.ipd_no) fd.append('ipd_no', String(row.ipd_no));

                  const itemsUrl = AppConst.BASE_URL + (AppConst.getIpdBillItemsUrl || '/api/get_ipd_bill_items');
                  const res = await axios.post(itemsUrl, fd);

                  if (res?.data?.status && Array.isArray(res.data.items)) {
                    const items = res.data.items;
                    const listEl = document.createElement('ul');
                    listEl.style.textAlign = 'left';
                    listEl.style.paddingLeft = '18px';
                    listEl.style.margin = '0';
                    if (items.length === 0) {
                      const li = document.createElement('li'); li.textContent = 'No services found'; listEl.appendChild(li);
                    } else {
                      items.forEach(it => { const li = document.createElement('li'); const svc = it.ipd_bill_item_service ?? it.service ?? it.name ?? 'Unknown service'; const price = Number(it.ipd_bill_item_price ?? it.price ?? 0).toFixed(2); li.innerHTML = `${svc} <small class="text-muted">(${price})</small>`; listEl.appendChild(li); });
                    }
                    Swal.fire({ title: `Services used — ${row.ipd_patient_name || ''}`, html: listEl, width: 700, showCloseButton: true, focusConfirm: false, confirmButtonText: 'Close' });
                  } else {
                    openPatientCard(row);
                  }
                } catch (err) {
                  console.error('Error fetching ipd bill items', err);
                  Swal.fire('Error', 'Could not load services. Please check console / network.', 'error');
                  openPatientCard(row);
                }
              }}
            />

            <div className="text-end mt-3 text-muted">
              <strong>Total IPD Bill:</strong> ₹{totals.totalIpdBill} &nbsp;|&nbsp;
              <strong>Commission:</strong>{" "}
  {(() => {
    const tc = Number(totals.totalCommission ?? 0);
    const style = {};
    if (tc === 0) {
      style.color = '#c92a2a';
      style.fontWeight = 700;
    } else if (tc > 1) {
      style.color = '#2f9e44';
      style.fontWeight = 700;
    }
    return <span style={style}>₹{(tc).toFixed(2)}</span>;
  })()} &nbsp;|&nbsp;
              <span style={{marginLeft:8}}>💵 <strong>Paid (Cash):</strong> ₹{totals.paidCash}</span> &nbsp;|&nbsp;
              <span style={{marginLeft:8}}>💳 <strong>Paid (Card):</strong> ₹{totals.paidCard}</span> &nbsp;|&nbsp;
              <span style={{marginLeft:8}}>🌐 <strong>Paid (Online):</strong> ₹{totals.paidOnline}</span> &nbsp;|&nbsp;
              <strong>Total Due:</strong> ₹{totals.totalDue} &nbsp;|&nbsp;
              <strong>Total Patients:</strong> {totals.totalPatients}
            </div>

          </div>
        </div>

      </div>

      {/* Pay Modal */}
      {payModalOpen && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handlePaySubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Pay Doctor Commission (IPD)</h5>
                  <button type="button" className="btn-close" onClick={closePayModal}></button>
                </div>
                <div className="modal-body">
                  {payRow && (
                    <>
                      <p className="mb-2"><strong>Doctor:</strong> {getDoctorNameForRow(payRow) || '-'}</p>
                      <p className="mb-2"><strong>Patient:</strong> {payRow.ipd_patient_name} ({payRow.ipd_uhid || ''})</p>
                      <p className="mb-2">
                        <strong>Commission:</strong> ₹{getDoctorNumbers(payRow).commission.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Paid:</strong> ₹{getDoctorNumbers(payRow).paid.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Due:</strong> ₹{getDoctorNumbers(payRow).due.toFixed(2)}
                      </p>

                      <div className="mb-3">
                        <label className="form-label">Pay Amount (₹)</label>
                        <input type="number" className="form-control" step="0.01" min="0" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                      </div>

                      {payError && <div className="alert alert-danger py-2">{payError}</div>}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={closePayModal} disabled={payProcessing}>Close</button>
                  <button type="submit" className="btn btn-primary" disabled={payProcessing}>{payProcessing ? "Saving..." : "Save Payment"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
